/**
 * Permission bitmask model (docs/requirements/01-data-model.md §3–4, database-schema.sql
 * `permission_bits` / `role_presets` / `effective_mask()` / `share_link_mask()`).
 *
 * Masks are int64 in the database (`bigint`) and plain JSON integers on the wire
 * (`PermissionMask: integer/int64` in the OpenAPI contract). Internally this module works in
 * `bigint` so any of the 64 bits can be set without precision loss; `serializeMask` /
 * `parseMask` convert at the boundary. postgres.js returns `bigint` columns as strings, which
 * `parseMask` accepts.
 */

/** Bit positions, exactly as seeded in `permission_bits`. Value = 1n << bit. */
export const PERMISSION_BIT_INDEX = {
  VIEW: 0,
  DOWNLOAD: 1,
  COMMENT: 2,
  UPLOAD: 3,
  EDIT_METADATA: 4,
  MANAGE_BOARD: 5,
  MANAGE_MEMBERS: 6,
  ADMIN: 7,
  SHARE: 8,
  APPROVE: 9,
  DELETE_ANY_COMMENT: 10,
} as const;

export type PermissionBitName = keyof typeof PERMISSION_BIT_INDEX;

/** Ordered list of defined bit names (bit 0 first). */
export const PERMISSION_BIT_NAMES = Object.keys(
  PERMISSION_BIT_INDEX,
) as readonly PermissionBitName[];

/** Bit values as bigint: `Permission.VIEW === 1n`, `Permission.DELETE_ANY_COMMENT === 1024n`. */
export const Permission: Readonly<Record<PermissionBitName, bigint>> = Object.freeze(
  Object.fromEntries(
    PERMISSION_BIT_NAMES.map((name) => [name, 1n << BigInt(PERMISSION_BIT_INDEX[name])]),
  ) as Record<PermissionBitName, bigint>,
);

/** Highest defined bit index; bits 11–63 are reserved. */
export const MAX_DEFINED_BIT = 10;

/** Mask with every defined bit set (0–10) = 2047n. */
export const ALL_PERMISSIONS: bigint = (1n << BigInt(MAX_DEFINED_BIT + 1)) - 1n;

/** Role presets from `role_presets` (01 §4). `guest` is the share-link base mask. */
export const ROLE_PRESETS = {
  owner: 2047n,
  admin: 2047n,
  editor: 831n,
  commenter: 7n,
  viewer: 3n,
  guest: 1n,
} as const satisfies Record<string, bigint>;

export type RolePresetName = keyof typeof ROLE_PRESETS;

export const ROLE_PRESET_NAMES = Object.keys(ROLE_PRESETS) as readonly RolePresetName[];

/** Anything a mask may arrive as: bigint (internal), number (JSON), string (postgres.js int8). */
export type MaskLike = bigint | number | string;

const INT64_MAX = (1n << 63n) - 1n;

/**
 * Normalise a mask from any transport representation to bigint.
 * Throws RangeError on negative, non-integer, or > int64 values.
 */
export function parseMask(input: MaskLike): bigint {
  let v: bigint;
  if (typeof input === 'bigint') v = input;
  else if (typeof input === 'number') {
    if (!Number.isInteger(input))
      throw new RangeError(`permission mask must be an integer, got ${input}`);
    v = BigInt(input);
  } else {
    const s = input.trim();
    if (!/^-?\d+$/.test(s))
      throw new RangeError(`permission mask must be an integer string, got "${input}"`);
    v = BigInt(s);
  }
  if (v < 0n) throw new RangeError('permission mask must be >= 0');
  if (v > INT64_MAX) throw new RangeError('permission mask exceeds int64');
  return v;
}

/**
 * Convert a mask to the JSON wire representation (`integer`). The contract types masks as
 * int64 but JSON numbers are only exact up to 2^53-1; masks using bits above 52 cannot be
 * represented and throw. Use `maskToString` for a lossless text form (DB params, logs).
 */
export function serializeMask(mask: MaskLike): number {
  const v = parseMask(mask);
  if (v > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new RangeError('permission mask does not fit a JSON-safe integer; use maskToString');
  }
  return Number(v);
}

/** Lossless decimal string (what postgres.js expects for int8 parameters). */
export function maskToString(mask: MaskLike): string {
  return parseMask(mask).toString(10);
}

/** Resolve a bit argument (name or bigint value) to its bigint value. */
export function bitValue(bit: PermissionBitName | bigint): bigint {
  return typeof bit === 'bigint' ? bit : Permission[bit];
}

/** `(mask & required) == required` (01 §3). `required` may be a single bit name or a mask of bits. */
export function has(mask: MaskLike, required: PermissionBitName | MaskLike): boolean {
  const m = parseMask(mask);
  const r =
    typeof required === 'string' && required in Permission
      ? Permission[required as PermissionBitName]
      : parseMask(required as MaskLike);
  return (m & r) === r;
}

/** Alias required by 01 §4 ("hasPermission(mask, bit)"). */
export const hasPermission = has;

/** Does the mask hold *every* listed bit? */
export function hasAll(mask: MaskLike, bits: readonly (PermissionBitName | bigint)[]): boolean {
  return has(mask, combine(bits));
}

/** Does the mask hold *at least one* of the listed bits? */
export function hasAny(mask: MaskLike, bits: readonly (PermissionBitName | bigint)[]): boolean {
  const m = parseMask(mask);
  return bits.some((b) => (m & bitValue(b)) !== 0n);
}

/** OR a list of bits (names or values) into one mask. */
export function combine(bits: readonly (PermissionBitName | bigint)[]): bigint {
  return bits.reduce<bigint>((acc, b) => acc | bitValue(b), 0n);
}

export function grant(mask: MaskLike, ...bits: (PermissionBitName | bigint)[]): bigint {
  return parseMask(mask) | combine(bits);
}

export function revoke(mask: MaskLike, ...bits: (PermissionBitName | bigint)[]): bigint {
  return parseMask(mask) & ~combine(bits);
}

/** Decode a mask into its defined bit names (bit order). Reserved bits are ignored. */
export function maskToBits(mask: MaskLike): PermissionBitName[] {
  const m = parseMask(mask);
  return PERMISSION_BIT_NAMES.filter((name) => (m & Permission[name]) !== 0n);
}

/** Encode bit names into a mask (inverse of `maskToBits`). */
export function bitsToMask(bits: readonly PermissionBitName[]): bigint {
  return combine(bits);
}

/** Bits in `mask` that are *not* defined (11–63). Non-empty means the mask is out of contract. */
export function reservedBits(mask: MaskLike): bigint {
  return parseMask(mask) & ~ALL_PERMISSIONS;
}

/**
 * `effective_mask(member, board)` semantics from database-schema.sql:
 * `COALESCE(nearest override on the board or an ancestor, role mask of an active member, 0)`.
 *
 * Pass the member's role mask (0 / null when the member is not active) and, when the caller
 * already resolved it, the nearest override mask. An override replaces the role mask entirely.
 */
export function effectiveMask(
  memberMask: MaskLike | null | undefined,
  overrideMask?: MaskLike | null,
): bigint {
  if (overrideMask !== undefined && overrideMask !== null) return parseMask(overrideMask);
  if (memberMask === undefined || memberMask === null) return 0n;
  return parseMask(memberMask);
}

export interface BoardOverrideCandidate {
  /** Depth of the board carrying the override (0 = root). Deeper wins. */
  depth: number;
  mask: MaskLike;
}

/**
 * Pick the nearest (deepest) override among those found on the board and its ancestors,
 * then apply `effectiveMask`. Mirrors `ORDER BY b.depth DESC LIMIT 1` in the SQL function.
 */
export function effectiveMaskFromOverrides(
  memberMask: MaskLike | null | undefined,
  overrides: readonly BoardOverrideCandidate[],
): bigint {
  let nearest: BoardOverrideCandidate | undefined;
  for (const o of overrides) if (!nearest || o.depth > nearest.depth) nearest = o;
  return effectiveMask(memberMask, nearest?.mask);
}

/** Workspace-level mask for a personal access token: PAT mask ∩ member role mask (api-conventions). */
export function intersectMasks(a: MaskLike, b: MaskLike): bigint {
  return parseMask(a) & parseMask(b);
}

/** `scope: asset` rule: max (union) of effective masks over containing boards the caller can VIEW. */
export function unionMasks(masks: readonly MaskLike[]): bigint {
  return masks.reduce<bigint>((acc, m) => acc | parseMask(m), 0n);
}

export interface ShareLinkFlags {
  allow_download: boolean;
  allow_comment: boolean;
}

/** `share_link_mask()`: VIEW + DOWNLOAD if allow_download + COMMENT if allow_comment (1/3/5/7). */
export function shareLinkMask(flags: ShareLinkFlags): bigint {
  let m = ROLE_PRESETS.guest;
  if (flags.allow_download) m |= Permission.DOWNLOAD;
  if (flags.allow_comment) m |= Permission.COMMENT;
  return m;
}

/** Guest upload form submitters: UPLOAD only, scoped to one board (01 §4). */
export const GUEST_UPLOAD_MASK: bigint = Permission.UPLOAD;

/** Only ADMIN may create roles carrying ADMIN or MANAGE_MEMBERS (01 §4, api-conventions). */
export const PRIVILEGED_ROLE_BITS: bigint = Permission.ADMIN | Permission.MANAGE_MEMBERS;

export function roleRequiresAdminToCreate(roleMask: MaskLike): boolean {
  return (parseMask(roleMask) & PRIVILEGED_ROLE_BITS) !== 0n;
}

/**
 * Build the `details` object of a 403 `permission_denied` error (api-conventions §Permissions).
 * Values are wire integers.
 */
export function permissionDeniedDetails(
  requiredMask: MaskLike,
  effective: MaskLike,
): {
  required_mask: number;
  effective_mask: number;
  missing_bits: PermissionBitName[];
} {
  const req = parseMask(requiredMask);
  const eff = parseMask(effective);
  return {
    required_mask: serializeMask(req),
    effective_mask: serializeMask(eff),
    missing_bits: maskToBits(req & ~eff),
  };
}
